import React, { useState, useEffect, useRef } from 'react';
import { ventesAPI, bijouxAPI, clientsAPI, boutiquesAPI } from '../../services/api';
import { ShoppingCart, Plus, X, Save, Calendar, CreditCard, User, Gem, Store, Eye, Printer, FileText, Star, Search, UserPlus, Pencil } from 'lucide-react';
import { generateFacturePDF, generateFacturePremiumPDF } from '../../utils/pdfGenerator';
import { getTypeCaisseLabel, getTypeCaisseBadgeClass, canSeeTypeCaisse, canEditVente, TYPE_CAISSE_OPTIONS } from '../../utils/typeCaisseLabels';
import EditVenteModal from './EditVenteModal';
import { AnomalieBadge, AnomalieDot, chargerAnomalies, estCentral } from '../Common/AnomalieBadge';

function VentesAvance() {
  const [ventes, setVentes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [bijoux, setBijoux] = useState([]);
  const [clients, setClients] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedVente, setSelectedVente] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFactureMenu, setShowFactureMenu] = useState(null);
  const [filtreBoutique, setFiltreBoutique] = useState('');
  const [filtrePeriode, setFiltrePeriode] = useState('tout'); // jour, semaine, mois, tout, custom — defaut 'tout' (le mois courant peut etre vide)
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  // §201 — Recherche serveur (debounce) + edition centrale
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingVente, setEditingVente] = useState(null);

  // §201 — Role pour masquage type_caisse + bouton Modifier (admin only)
  const role = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.role || ''; }
    catch { return ''; }
  })();
  const showTypeCaisse = canSeeTypeCaisse(role);
  const showEdit = canEditVente(role);

  // Recherche bijou visuelle
  const [bijouSearch, setBijouSearch] = useState('');
  const [bijouSearchResults, setBijouSearchResults] = useState([]);
  const [selectedBijou, setSelectedBijou] = useState(null);

  const [newVente, setNewVente] = useState({
    bijou_id: '',
    client_id: '',
    boutique_id: '',
    prix_vente: '',
    mode_paiement: 'especes', // P6 caisse 11/06/2026 — fin des especes_caisse1/2
    type_caisse: 'officielle', // §201 — defaut vendeuse ; selecteur admin only
    date_vente: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Gestion client : recherche existant / nouveau
  const [clientMode, setClientMode] = useState('existant'); // 'existant' | 'nouveau'
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [selectedClientLabel, setSelectedClientLabel] = useState('');
  const [nouveauClient, setNouveauClient] = useState({ nom: '', prenom: '', telephone: '', email: '' });
  const clientSearchRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  // Alertes anomalies A6 — modes de paiement « inconnu » (lot 1, central only §205)
  const [anomalies, setAnomalies] = useState({});
  const loadAnomalies = async () => { if (estCentral()) setAnomalies(await chargerAnomalies(['A6'])); };
  useEffect(() => { loadAnomalies(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const anomParVente = {};
  (anomalies.A6?.items || []).forEach(i => { anomParVente[i.entite_id] = i; });

  // §201 — debounce de la barre de recherche (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // §201 — (re)charge les ventes cote serveur a chaque changement de recherche.
  // Fire aussi au montage avec '' => liste complete.
  useEffect(() => {
    loadVentes(debouncedSearch);
  }, [debouncedSearch]);

  // Fermer le menu de facture quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFactureMenu && !event.target.closest('.facture-menu-container')) {
        setShowFactureMenu(null);
      }
    };

    if (showFactureMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showFactureMenu]);

  // Donnees de reference (bijoux/clients/boutiques) — chargees une fois.
  // Les ventes sont gerees a part par loadVentes (recherche serveur §201).
  const loadData = async () => {
    try {
      const [bijouxRes, clientsRes, boutiquesRes] = await Promise.all([
        // §113c - limit=2000 (plafond backend 500 par défaut) ; PAS de stock_only :
        // la liste sert aussi à générer la facture PDF de ventes passées (bijou vendu)
        bijouxAPI.getAll({ limit: 2000 }),
        clientsAPI.getAll(),
        boutiquesAPI.getAll()
      ]);
      setBijoux(bijouxRes.data);
      setClients(clientsRes.data);
      setBoutiques(boutiquesRes.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // §201 — charge les ventes avec recherche serveur (?search=...)
  const loadVentes = async (searchTerm = '') => {
    try {
      const params = searchTerm ? { search: searchTerm } : undefined;
      const res = await ventesAPI.getAll(params);
      setVentes(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Erreur chargement ventes:', error);
      setVentes([]);
    }
  };

  const handleBijouSearch = (val) => {
    setBijouSearch(val);
    setSelectedBijou(null);
    setNewVente(v => ({ ...v, bijou_id: '', prix_vente: '' }));
    if (!val.trim()) { setBijouSearchResults([]); return; }
    const q = val.toLowerCase();
    const results = bijoux.filter(b => b.inventaire_actif === 1 && (
      (b.reference || '').toLowerCase().includes(q) ||
      (b.reference_unique || '').toLowerCase().includes(q) ||
      (b.reference_article || '').toLowerCase().includes(q) ||
      (b.nom || '').toLowerCase().includes(q) ||
      (b.id_produit || '').toLowerCase().includes(q) ||
      (b.description || '').toLowerCase().includes(q)
    )).slice(0, 10);
    setBijouSearchResults(results);
  };

  const selectBijou = (bijou) => {
    setSelectedBijou(bijou);
    setNewVente(v => ({ ...v, bijou_id: bijou.id, prix_vente: bijou.prix_vente || bijou.pvp_magasin || '' }));
    setBijouSearch('');
    setBijouSearchResults([]);
  };

  // Recherche client en temps réel
  const handleClientSearch = (val) => {
    setClientSearch(val);
    setSelectedClientLabel('');
    setNewVente(v => ({ ...v, client_id: '' }));
    if (!val.trim()) { setClientSearchResults([]); return; }
    const q = val.toLowerCase();
    const results = clients.filter(c =>
      (c.nom || '').toLowerCase().includes(q) ||
      (c.prenom || '').toLowerCase().includes(q) ||
      (c.telephone || '').includes(q) ||
      (`${c.prenom || ''} ${c.nom || ''}`).toLowerCase().includes(q)
    ).slice(0, 8);
    setClientSearchResults(results);
  };

  const selectClient = (client) => {
    setNewVente(v => ({ ...v, client_id: client.id }));
    setSelectedClientLabel(`${client.prenom || ''} ${client.nom}`.trim() + (client.telephone ? ` — ${client.telephone}` : ''));
    setClientSearch('');
    setClientSearchResults([]);
  };

  const handleCreateVente = async () => {
    if (!newVente.bijou_id || !newVente.boutique_id) {
      alert('Veuillez sélectionner un bijou et une boutique');
      return;
    }

    setSaving(true);
    try {
      let clientId = newVente.client_id;

      // Créer le client à la volée si mode nouveau
      if (clientMode === 'nouveau') {
        if (!nouveauClient.nom.trim()) {
          alert('Le nom du client est requis');
          setSaving(false);
          return;
        }
        const res = await clientsAPI.create({
          nom: `${nouveauClient.prenom.trim()} ${nouveauClient.nom.trim()}`.trim(),
          telephone: nouveauClient.telephone || null,
          email: nouveauClient.email || null,
        });
        clientId = res.data.id;
        await loadData(); // refresh la liste clients
      }

      if (!clientId) {
        alert('Veuillez sélectionner ou créer un client');
        setSaving(false);
        return;
      }

      await ventesAPI.create({
        ...newVente,
        bijou_id: parseInt(newVente.bijou_id),
        client_id: parseInt(clientId),
        boutique_id: parseInt(newVente.boutique_id),
        prix_vente: parseFloat(newVente.prix_vente)
      });

      setShowCreateModal(false);
      setNewVente({ bijou_id: '', client_id: '', boutique_id: '', prix_vente: '', mode_paiement: 'especes', type_caisse: 'officielle', date_vente: new Date().toISOString().split('T')[0], notes: '' });
      setClientMode('existant');
      setClientSearch('');
      setSelectedClientLabel('');
      setNouveauClient({ nom: '', prenom: '', telephone: '', email: '' });
      setBijouSearch('');
      setSelectedBijou(null);
      setBijouSearchResults([]);
      await Promise.all([loadData(), loadVentes(debouncedSearch)]);
    } catch (error) {
      console.error('Erreur création vente:', error);
      alert(error.response?.data?.error || 'Erreur lors de la création de la vente');
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetails = (vente) => {
    setSelectedVente(vente);
    setShowDetailModal(true);
  };

  // Génère facture avec bijou complet (cherche dans la liste chargée)
  const handleGenererFacture = (vente, premium = false) => {
    const bijouComplet = bijoux.find(b => b.id === vente.bijou_id) || {};
    if (premium) {
      generateFacturePremiumPDF(vente, bijouComplet);
    } else {
      generateFacturePDF(vente);
    }
    setShowFactureMenu(null);
  };

  const getModesPaiementBadge = (mode) => {
    const colors = {
      especes_caisse1: 'bg-green-100 text-green-800',
      especes_caisse2: 'bg-gray-100 text-gray-800',
      especes: 'bg-green-100 text-green-800',
      virement: 'bg-blue-100 text-blue-800',
      cheque: 'bg-yellow-100 text-yellow-800',
      carte: 'bg-purple-100 text-purple-800',
      or: 'bg-amber-100 text-amber-800',
      compte: 'bg-indigo-100 text-indigo-800',
      autre: 'bg-gray-100 text-gray-800'
    };
    return colors[mode] || 'bg-gray-100 text-gray-800';
  };

  // Filtre par date
  const getDateRange = () => {
    const now = new Date();
    // Date locale (pas UTC) pour eviter le decalage de timezone
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    if (filtrePeriode === 'jour') return { from: todayStr, to: todayStr };
    if (filtrePeriode === 'semaine') {
      const d = new Date(now); d.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay() - 1)); // lundi
      const lundiStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      return { from: lundiStr, to: todayStr };
    }
    if (filtrePeriode === 'mois') {
      return { from: todayStr.slice(0, 8) + '01', to: todayStr };
    }
    if (filtrePeriode === 'custom') return { from: dateDebut, to: dateFin };
    return { from: '', to: '' }; // tout
  };
  const dateRange = getDateRange();

  // Convertir date_vente en YYYY-MM-DD local (gere tous les formats)
  const toDateStr = (d) => {
    if (!d) return '';
    // Si deja au format YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
    // Sinon parser comme Date
    const dt = new Date(d);
    if (isNaN(dt)) return '';
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
  };

  // Filtre par boutique + date
  // §201 — quand une recherche serveur est active, on ignore le filtre periode
  // (equivalent "Tout") pour ne pas masquer des resultats plus anciens.
  const searchActive = !!debouncedSearch;
  const ventesFiltrees = ventes.filter(v => {
    if (filtreBoutique && String(v.boutique_id) !== filtreBoutique) return false;
    if (!searchActive) {
      const vDate = toDateStr(v.date_vente);
      if (dateRange.from && vDate < dateRange.from) return false;
      if (dateRange.to && vDate > dateRange.to) return false;
    }
    return true;
  });
  const totalVentes = ventesFiltrees.reduce((sum, v) => sum + parseFloat(v.prix_vente || 0), 0);

  // Grouper par boutique pour affichage
  const isCtx = (() => { try { return JSON.parse(localStorage.getItem('workspaceContext') || '{}').type; } catch { return ''; } })();
  const isCentral = isCtx === 'central';
  const boutiquesUniques = [...new Set(ventes.map(v => v.boutique_nom).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center space-x-3">
          <ShoppingCart className="h-8 w-8 text-green-600" />
          <div>
            <h2 className="text-3xl font-bold text-gray-800">Historique des ventes</h2>
            {anomalies.A6 && (
              <AnomalieBadge type="A6" titre={anomalies.A6.titre}
                explication={anomalies.A6.explication} items={anomalies.A6.items} onChange={loadAnomalies} />
            )}
            <p className="text-sm text-gray-600 mt-1">
              {ventesFiltrees.length} vente{ventesFiltrees.length > 1 ? 's' : ''} • Total: {totalVentes.toLocaleString()} DH
            </p>
          </div>
        </div>
        {/* Filtre par boutique (visible en mode central) */}
        {isCentral && boutiquesUniques.length > 1 && (
          <select value={filtreBoutique} onChange={e => setFiltreBoutique(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium">
            <option value="">Toutes les boutiques</option>
            {boutiques.map(b => (
              <option key={b.id} value={b.id}>{b.nom}</option>
            ))}
          </select>
        )}
      </div>

      {/* Filtres par periode */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { key: 'jour', label: "Aujourd'hui" },
          { key: 'semaine', label: 'Cette semaine' },
          { key: 'mois', label: 'Ce mois' },
          { key: 'tout', label: 'Tout' },
          { key: 'custom', label: 'Personnalise' },
        ].map(p => (
          <button key={p.key} onClick={() => setFiltrePeriode(p.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filtrePeriode === p.key
                ? 'bg-green-600 text-white shadow'
                : 'bg-white text-gray-600 border hover:bg-gray-50'
            }`}>
            {p.label}
          </button>
        ))}
        {filtrePeriode === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm" />
            <span className="text-gray-400">au</span>
            <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm" />
          </div>
        )}
      </div>

      {/* §201 — Barre de recherche serveur (client, tel, bijou, ref, montant) */}
      <div className="relative w-full md:w-[30rem]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher client, téléphone, bijou, référence, montant…"
          className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Effacer"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Sous-totaux par boutique en mode central */}
      {isCentral && !filtreBoutique && boutiquesUniques.length > 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {boutiquesUniques.map(bNom => {
            const bVentes = ventesFiltrees.filter(v => v.boutique_nom === bNom);
            const bTotal = bVentes.reduce((s, v) => s + parseFloat(v.prix_vente || 0), 0);
            return (
              <div key={bNom} className="bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => {
                  const b = boutiques.find(x => x.nom === bNom);
                  if (b) setFiltreBoutique(String(b.id));
                }}>
                <div className="flex items-center space-x-3">
                  <Store className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-bold text-gray-900">{bNom}</p>
                    <p className="text-xs text-gray-500">{bVentes.length} vente{bVentes.length > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <p className="text-lg font-black text-green-700">{bTotal.toLocaleString()} DH</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Totaux par mode de paiement */}
      {ventesFiltrees.length > 0 && (() => {
        // Ventilation DYNAMIQUE par mode reel (aucun mode ignore : 'inconnu', 'multiple'...
        // tombaient dans le vide avant -> TOTAL faux a 3 976 au lieu de 3 394 498).
        // Base = prix_vente, la MEME mesure que l'entete -> coherence garantie :
        // TOTAL bloc == total entete == somme des pastilles.
        const MODE_INFO = {
          especes:  { label: 'Espèces',  color: 'bg-green-100 text-green-700 border-green-200' },
          carte:    { label: 'Carte',    color: 'bg-blue-100 text-blue-700 border-blue-200' },
          cheque:   { label: 'Chèque',   color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
          virement: { label: 'Virement', color: 'bg-purple-100 text-purple-700 border-purple-200' },
          or:       { label: 'Or',       color: 'bg-amber-100 text-amber-700 border-amber-200' },
          compte:   { label: 'Compte',   color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
          credit:   { label: 'Crédit',   color: 'bg-pink-100 text-pink-700 border-pink-200' },
          multiple: { label: 'Multiple', color: 'bg-teal-100 text-teal-700 border-teal-200' },
          inconnu:  { label: 'Inconnu',  color: 'bg-gray-100 text-gray-600 border-gray-200' },
        };
        const totaux = {};
        ventesFiltrees.forEach(v => {
          const brut = (v.mode_paiement || 'inconnu').toLowerCase();
          const mode = brut.includes('especes') ? 'especes' : brut;
          totaux[mode] = (totaux[mode] || 0) + parseFloat(v.prix_vente || 0);
        });
        const cles = Object.keys(totaux).sort((a, b) => totaux[b] - totaux[a]);
        return (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex flex-wrap gap-3 items-center">
              {cles.map(k => {
                const info = MODE_INFO[k] || { label: k.charAt(0).toUpperCase() + k.slice(1), color: 'bg-gray-100 text-gray-600 border-gray-200' };
                return (
                  <div key={k} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${info.color}`}>
                    <span className="text-xs font-medium">{info.label}</span>
                    <span className="text-sm font-black">{totaux[k].toLocaleString('fr-FR')} DH</span>
                  </div>
                );
              })}
              <div className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white">
                <span className="text-xs font-medium">TOTAL</span>
                <span className="text-lg font-black">{totalVentes.toLocaleString('fr-FR')} DH</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Message si aucune vente */}
      {ventesFiltrees.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-lg font-medium">Aucune vente sur cette periode</p>
          <p className="text-gray-400 text-sm mt-1">Essayez un autre filtre ou une autre periode</p>
          <div className="flex justify-center gap-2 mt-4">
            <button onClick={() => setFiltrePeriode('mois')} className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200">Ce mois</button>
            <button onClick={() => { setFiltrePeriode('tout'); setFiltreBoutique(''); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">Tout voir</button>
          </div>
        </div>
      )}

      {/* Mobile cards — ventes */}
      <div className="md:hidden space-y-3">
        {ventesFiltrees.map(vente => (
          <div key={vente.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-green-400">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-bold text-gray-900">
                  {vente.bijou_nom}
                  <AnomalieDot type="A6" item={anomParVente[vente.id]} onChange={loadAnomalies} />
                </div>
                <div className="text-xs text-gray-500">{vente.client_nom} • {vente.boutique_nom}</div>
                <div className="text-xs text-gray-400 mt-0.5">{new Date(vente.date_vente).toLocaleDateString('fr-FR')}</div>
              </div>
              {vente.paiements && vente.paiements.length > 0 ? (
                <div className="flex flex-wrap gap-1 justify-end">
                  {vente.paiements.map((p, idx) => (
                    <span key={idx} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getModesPaiementBadge(p.mode)}`}>
                      {p.mode === 'especes' ? 'Esp.' : p.mode === 'carte' ? 'CB' : p.mode === 'cheque' ? 'Chq' : p.mode === 'virement' ? 'Vir.' : p.mode === 'or' ? 'Or' : p.mode === 'compte' ? 'Cpt' : p.mode} {parseFloat(p.montant).toLocaleString()}
                    </span>
                  ))}
                </div>
              ) : (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getModesPaiementBadge(vente.mode_paiement)}`}>
                  {vente.mode_paiement === 'especes_caisse1' ? 'Espèces C1' :
                   vente.mode_paiement === 'especes_caisse2' ? 'Caisse 2' :
                   vente.mode_paiement === 'virement' ? 'Virement' :
                   vente.mode_paiement === 'cheque' ? 'Chèque' :
                   vente.mode_paiement === 'carte' ? 'Carte' : 'Autre'}
                </span>
              )}
            </div>
            <div className="flex justify-between items-center mt-2">
              <div className="flex items-center gap-2">
                <div className="text-sm font-bold text-green-600">{parseFloat(vente.prix_vente).toLocaleString()} DH</div>
                {showTypeCaisse && vente.type_caisse && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getTypeCaisseBadgeClass(vente.type_caisse)}`}>
                    {getTypeCaisseLabel(vente.type_caisse)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {showEdit && (
                  <button onClick={() => setEditingVente(vente)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm flex items-center gap-1">
                    <Pencil className="h-4 w-4" /> Modifier
                  </button>
                )}
                <button onClick={() => handleViewDetails(vente)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm">Voir</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tableau des ventes */}
      <div className="hidden md:block bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1024px' }}>
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Boutique</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bijou</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Paiement</th>
              {showTypeCaisse && <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Caisse</th>}
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap sticky right-0 z-20 bg-gray-100 shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.15)]">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {ventesFiltrees.map((vente) => (
              <tr key={vente.id} className="group hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>{new Date(vente.date_vente).toLocaleDateString('fr-FR')}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Store className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-gray-900">{vente.boutique_nom}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-purple-600" />
                    <span className="font-medium text-gray-900">{vente.client_nom}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Gem className="h-4 w-4 text-amber-600" />
                    <span className="text-gray-700">{vente.bijou_nom}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  <span className="font-bold text-green-600 text-lg">
                    {parseFloat(vente.prix_vente).toLocaleString()} DH
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-center">
                  {vente.paiements && vente.paiements.length > 0 ? (
                    <div className="flex flex-wrap justify-center gap-1">
                      {vente.paiements.map((p, idx) => (
                        <span key={idx} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getModesPaiementBadge(p.mode)}`}>
                          {p.mode === 'especes' ? 'Esp.' : p.mode === 'carte' ? 'CB' : p.mode === 'cheque' ? 'Chq' : p.mode === 'virement' ? 'Vir.' : p.mode === 'or' ? 'Or' : p.mode === 'compte' ? 'Cpt' : p.mode} {parseFloat(p.montant).toLocaleString()}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getModesPaiementBadge(vente.mode_paiement)}`}>
                      {vente.mode_paiement === 'especes_caisse1' ? 'Espèces C1' :
                       vente.mode_paiement === 'especes_caisse2' ? 'Espèces C2 (noir)' :
                       vente.mode_paiement === 'virement' ? 'Virement' :
                       vente.mode_paiement === 'cheque' ? 'Chèque' :
                       vente.mode_paiement === 'carte' ? 'Carte' : 'Autre'}
                    </span>
                  )}
                </td>
                {showTypeCaisse && (
                  <td className="px-6 py-4 text-sm text-center">
                    {vente.type_caisse ? (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getTypeCaisseBadgeClass(vente.type_caisse)}`}>
                        {getTypeCaisseLabel(vente.type_caisse)}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                )}
                <td className="px-6 py-4 text-sm text-center sticky right-0 z-10 bg-white group-hover:bg-gray-50 shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.15)]">
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={() => handleViewDetails(vente)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Voir détails"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    {showEdit && (
                      <button
                        onClick={() => setEditingVente(vente)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Modifier la vente"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                    )}
                    
                    <div className="relative facture-menu-container">
                      <button
                        onClick={() => setShowFactureMenu(showFactureMenu === vente.id ? null : vente.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Options de facture"
                      >
                        <Printer className="h-5 w-5" />
                      </button>
                      
                      {showFactureMenu === vente.id && (
                        <div className="absolute right-0 top-12 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px]">
                          <div className="p-2">
                            <button
                              onClick={() => handleGenererFacture(vente, false)}
                              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                            >
                              <FileText className="h-4 w-4" />
                              <span>Facture Standard</span>
                            </button>
                            <button
                              onClick={() => handleGenererFacture(vente, true)}
                              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                            >
                              <Star className="h-4 w-4 text-yellow-500" />
                              <span>Facture Premium (detaillee)</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de création de vente */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 flex justify-between items-center rounded-t-lg">
              <div className="flex items-center space-x-3">
                <Plus className="h-6 w-6" />
                <h3 className="text-xl font-bold">Nouvelle vente</h3>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Sélection bijou — recherche visuelle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Gem className="h-4 w-4 text-yellow-600" /> Bijou *
                </label>
                {selectedBijou ? (
                  <div className="flex items-center gap-3 p-3 border-2 border-yellow-400 bg-yellow-50 rounded-xl">
                    {selectedBijou.image_url ? (
                      <img src={selectedBijou.image_url} alt="" className="h-16 w-16 object-cover rounded-lg flex-shrink-0" />
                    ) : (
                      <div className="h-16 w-16 bg-gradient-to-br from-yellow-200 to-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Gem className="h-8 w-8 text-yellow-700" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 truncate">{selectedBijou.nom}</p>
                      <p className="text-xs text-yellow-700 font-mono">{selectedBijou.reference || selectedBijou.id_produit}</p>
                      <p className="text-xs text-gray-500 truncate">{selectedBijou.description || selectedBijou.categorie}</p>
                      <p className="text-sm font-bold text-green-700 mt-0.5">{parseFloat(selectedBijou.prix_vente || selectedBijou.pvp_magasin || 0).toLocaleString()} DH</p>
                    </div>
                    <button type="button" onClick={() => { setSelectedBijou(null); setNewVente(v => ({ ...v, bijou_id: '', prix_vente: '' })); }}
                      className="p-1.5 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={bijouSearch}
                        onChange={e => handleBijouSearch(e.target.value)}
                        placeholder="Référence, nom, description..."
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                      />
                    </div>
                    {bijouSearchResults.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-y-auto">
                        {bijouSearchResults.map(b => (
                          <button key={b.id} type="button" onClick={() => selectBijou(b)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-yellow-50 border-b border-gray-100 last:border-0 text-left transition-colors">
                            {b.image_url ? (
                              <img src={b.image_url} alt="" className="h-12 w-12 object-cover rounded-lg flex-shrink-0" />
                            ) : (
                              <div className="h-12 w-12 bg-gradient-to-br from-yellow-100 to-yellow-300 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Gem className="h-6 w-6 text-yellow-600" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm truncate">{b.nom}</p>
                              <p className="text-xs text-yellow-700 font-mono">{b.reference || b.id_produit}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs font-bold text-green-700">{parseFloat(b.prix_vente || b.pvp_magasin || 0).toLocaleString()} DH</span>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs text-gray-500">{b.boutique_nom || 'Catalogue'}</span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {bijouSearch.length >= 2 && bijouSearchResults.length === 0 && (
                      <p className="text-xs text-gray-400 mt-1 px-1">Aucun bijou trouvé avec ce critère</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                  {/* Toggle existant / nouveau */}
                  <div className="flex gap-1 mb-2">
                    <button type="button"
                      onClick={() => { setClientMode('existant'); setNouveauClient({ nom: '', prenom: '', telephone: '', email: '' }); }}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs rounded-lg border transition-colors ${clientMode === 'existant' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                    >
                      <Search className="h-3 w-3" /> Client existant
                    </button>
                    <button type="button"
                      onClick={() => { setClientMode('nouveau'); setClientSearch(''); setClientSearchResults([]); setSelectedClientLabel(''); setNewVente(v => ({ ...v, client_id: '' })); }}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs rounded-lg border transition-colors ${clientMode === 'nouveau' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                    >
                      <UserPlus className="h-3 w-3" /> Nouveau client
                    </button>
                  </div>

                  {clientMode === 'existant' ? (
                    <div className="relative" ref={clientSearchRef}>
                      {selectedClientLabel ? (
                        <div className="flex items-center gap-2 px-3 py-2 border border-green-400 bg-green-50 rounded-lg">
                          <User className="h-4 w-4 text-green-600 shrink-0" />
                          <span className="text-sm text-green-800 flex-1">{selectedClientLabel}</span>
                          <button type="button" onClick={() => { setSelectedClientLabel(''); setNewVente(v => ({ ...v, client_id: '' })); }} className="text-gray-400 hover:text-red-500">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              value={clientSearch}
                              onChange={(e) => handleClientSearch(e.target.value)}
                              placeholder="Nom, prénom ou téléphone..."
                              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                            />
                          </div>
                          {clientSearchResults.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {clientSearchResults.map(c => (
                                <button key={c.id} type="button" onClick={() => selectClient(c)}
                                  className="w-full text-left px-3 py-2 hover:bg-green-50 text-sm border-b border-gray-100 last:border-0">
                                  <span className="font-medium">{c.prenom || ''} {c.nom}</span>
                                  {c.telephone && <span className="text-gray-400 ml-2 text-xs">{c.telephone}</span>}
                                </button>
                              ))}
                            </div>
                          )}
                          {clientSearch.length >= 2 && clientSearchResults.length === 0 && (
                            <p className="text-xs text-gray-400 mt-1 px-1">Aucun client trouvé — essayez "Nouveau client"</p>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input type="text" placeholder="Prénom" value={nouveauClient.prenom}
                          onChange={e => setNouveauClient(n => ({ ...n, prenom: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                        <input type="text" placeholder="Nom *" value={nouveauClient.nom}
                          onChange={e => setNouveauClient(n => ({ ...n, nom: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                      <input type="tel" placeholder="Téléphone" value={nouveauClient.telephone}
                        onChange={e => setNouveauClient(n => ({ ...n, telephone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                  )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Boutique *</label>
                  <select
                    value={newVente.boutique_id}
                    onChange={(e) => setNewVente({...newVente, boutique_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Sélectionner une boutique</option>
                    {boutiques.map(boutique => (
                      <option key={boutique.id} value={boutique.id}>
                        {boutique.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix de vente (DH) *</label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    value={newVente.prix_vente}
                    onChange={(e) => setNewVente({...newVente, prix_vente: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                  <select
                    value={newVente.mode_paiement}
                    onChange={(e) => setNewVente({...newVente, mode_paiement: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    {/* P6 caisse 11/06/2026 : mode = MOYEN de paiement ; la caisse
                        fiscale est un champ séparé (fin des especes_caisse1/2) */}
                    <option value="especes">Espèces</option>
                    <option value="carte">Carte</option>
                    <option value="cheque">Chèque</option>
                    <option value="virement">Virement bancaire</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de vente</label>
                  <input
                    type="date"
                    value={newVente.date_vente}
                    onChange={(e) => setNewVente({...newVente, date_vente: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* §201 — Type de caisse : selecteur admin only ; vendeuse = 'officielle' (defaut state) */}
              {showTypeCaisse && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de caisse</label>
                  <select
                    value={newVente.type_caisse}
                    onChange={(e) => setNewVente({ ...newVente, type_caisse: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    {TYPE_CAISSE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={newVente.notes}
                  onChange={(e) => setNewVente({...newVente, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="3"
                  placeholder="Notes supplémentaires..."
                />
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t rounded-b-lg">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateVente}
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    <span>Enregistrer la vente</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails */}
      {showDetailModal && selectedVente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex justify-between items-center rounded-t-lg">
              <div className="flex items-center space-x-3">
                <Eye className="h-6 w-6" />
                <h3 className="text-xl font-bold">Détails de la vente</h3>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Date de vente</div>
                  <div className="font-semibold text-gray-900">
                    {new Date(selectedVente.date_vente).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Prix de vente</div>
                  <div className="font-bold text-green-600 text-2xl">
                    {parseFloat(selectedVente.prix_vente).toLocaleString()} DH
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-700 mb-3">Informations</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Boutique:</span>
                    <span className="font-medium">{selectedVente.boutique_nom}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Client:</span>
                    <span className="font-medium">{selectedVente.client_nom}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bijou:</span>
                    <span className="font-medium">{selectedVente.bijou_nom}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mode de paiement:</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getModesPaiementBadge(selectedVente.mode_paiement)}`}>
                      {selectedVente.mode_paiement === 'especes' ? 'Espèces' :
                       selectedVente.mode_paiement === 'carte' ? 'Carte' :
                       selectedVente.mode_paiement === 'cheque' ? 'Chèque' : 'Virement'}
                    </span>
                  </div>
                  {selectedVente.paiements && selectedVente.paiements.length > 0 && (
                    <div className="mt-3 bg-blue-50 rounded-lg p-3">
                      <div className="text-sm font-semibold text-blue-800 mb-2">Detail des paiements</div>
                      <div className="space-y-1.5">
                        {selectedVente.paiements.map((p, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getModesPaiementBadge(p.mode)}`}>
                              {p.mode === 'especes' ? 'Especes' : p.mode === 'carte' ? 'Carte' : p.mode === 'cheque' ? 'Cheque' : p.mode === 'virement' ? 'Virement' : p.mode === 'or' ? 'Or' : p.mode === 'compte' ? 'Compte' : p.mode}
                            </span>
                            <span className="font-semibold text-gray-800">{parseFloat(p.montant).toLocaleString()} DH</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedVente.notes && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Notes</h4>
                  <p className="text-gray-600 text-sm">{selectedVente.notes}</p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t rounded-b-lg">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      {/* §201 — Modal d'edition (admin only) */}
      {editingVente && (
        <EditVenteModal
          vente={editingVente}
          onClose={() => setEditingVente(null)}
          onSaved={(updated) => {
            setVentes((list) =>
              list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x))
            );
          }}
        />
      )}
    </div>
  );
}

export default VentesAvance;
